import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const userRoleQueryKeys = {
  all: ['userRole'],
  roles: () => [...userRoleQueryKeys.all, 'roles'],
  users: () => [...userRoleQueryKeys.all, 'users'],
  userDetail: (userId) => [...userRoleQueryKeys.all, 'user', userId],
};

/**
 * 모든 역할 조회
 * GET /drivecore/id/api/UserRole/getroles
 */
export const useUserRoles = () => {
  return useQuery({
    queryKey: userRoleQueryKeys.roles(),
    queryFn: async () => {
      const response = await api.get('/drivecore/id/api/UserRole/getroles');
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지
    cacheTime: 10 * 60 * 1000, // 10분간 캐시 보관
  });
};

/**
 * 모든 사용자 조회
 * GET /drivecore/id/api/User
 */
export const useUsers = () => {
  return useQuery({
    queryKey: userRoleQueryKeys.users(),
    queryFn: async (params) => {
      const response = await api.get('/drivecore/id/api/User', { params });
      return response;
    },
    staleTime: 1 * 60 * 1000, // 1분간 캐시 유지
  });
};

/**
 * 사용자 역할 업데이트
 * PUT /drivecore/id/api/UserRole/updateuserroles (예상 엔드포인트)
 */
export const useUpdateUserRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roles }) => {
      const response = await api.put('/drivecore/id/api/UserRole/updateuserroles', {
        UserId: userId,
        Roles: roles,
      });
      return response;
    },
    onSuccess: () => {
      // 사용자 목록 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.users());
    },
  });
};


/**
 * 역할 추가
 * POST /drivecore/id/api/UserRole/addroles
 */
export const useAddRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleNames) => {
      // roleNames는 문자열 배열: ["string"]
      const response = await api.post('/drivecore/id/api/UserRole/addroles', roleNames);
      return response;
    },
    onSuccess: () => {
      // 역할 목록 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.roles());
    },
  });
};

/**
 * 역할 삭제
 * POST /drivecore/id/api/UserRole/removeroles
 */
export const useRemoveRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleNames) => {
      // roleNames는 문자열 배열: ["string"]
      const response = await api.post('/drivecore/id/api/UserRole/removeroles', roleNames);
      return response;
    },
    onSuccess: () => {
      // 역할 목록 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.roles());
    },
  });
};

/**
 * 역할 이름 수정
 * PUT /drivecore/id/api/UserRole/renamerole
 */
export const useRenameRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ originalName, newName }) => {
      const response = await api.put('/drivecore/id/api/UserRole/renamerole', {
        originalName,
        newName,
      });
      return response;
    },
    onSuccess: () => {
      // 역할 목록 및 역할-피처 매핑 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.roles());
    },
  });
};

/**
 * 사용자에게 역할 부여
 * POST /drivecore/id/api/UserRole/setrole
 */
export const useSetRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleName, userSysIds }) => {
      const response = await api.post('/drivecore/id/api/UserRole/setrole', {
        roleName,
        userSysIds,
      });
      return response;
    },
    onSuccess: () => {
      // 사용자 목록 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.users());
    },
  });
};

/**
 * 사용자에게 역할 제거
 * POST /drivecore/id/api/UserRole/unsetrole
 */
export const useUnsetRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleName, userSysIds }) => {
      const response = await api.post('/drivecore/id/api/UserRole/unsetrole', {
        roleName,
        userSysIds,
      });
      return response;
    },
    onSuccess: () => {
      // 사용자 목록 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.users());
    },
  });
};

/**
 * 사용자 추가
 * POST /drivecore/id/api/SystemAdmin/adduser
 */
export const useAddUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, password }) => {
      const response = await api.post('/drivecore/id/api/SystemAdmin/adduser', {
        email,
        password,
      });
      return response;
    },
    onSuccess: () => {
      // 사용자 목록 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.users());
    },
  });
};

/**
 * 사용자 삭제
 * DELETE /drivecore/id/api/SystemAdmin/deluser/{UserSysId}
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userSysId) => {
      const response = await api.delete(`/drivecore/id/api/SystemAdmin/deluser/${userSysId}`);
      return response;
    },
    onSuccess: () => {
      // 사용자 목록 캐시 무효화
      queryClient.invalidateQueries(userRoleQueryKeys.users());
    },
  });
};

